import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IAccessChangeStudent } from 'src/app/core/models/access';
import { ILearnerAccess, IStudent, UserAvatarData } from 'src/app/core/models/user';
import { IAttendanceRecord } from '../../models/academic';
import { selectLoadingInitialGuardian, selectLoggedInGuardian } from '../guardian/guardian.selectors';
import { StudentState } from './student.reducer';

export const selectStudentState = createFeatureSelector<StudentState>('students');

export const selectAllStudents = createSelector(selectStudentState, (state: StudentState) => state.students);

export const selectStudentsForAccesschange = createSelector(
  selectStudentState,
  (state: StudentState): IAccessChangeStudent[] => state.studentsForAccesschange,
);

export const selectSelectedStudentId = createSelector(
  selectStudentState,
  (state: StudentState) => state.selectedStudentId ?? '',
);

export const selectSelectedStudent = createSelector(selectStudentState, (state: StudentState) => {
  return state.selectedStudentId
    ? state.students.find((student) => student.id === state.selectedStudentId) || null
    : null;
});

export const selectStudentMedicalInfo = createSelector(selectSelectedStudent, (student) => student?.medicalInfo);

export const selectAllStudentIds = createSelector(selectAllStudents, (students) => {
  if (!students) return [];
  return students.map((student) => student.id);
});

export const selectStudentAttendanceFilters = createSelector(
  selectStudentState,
  (state: StudentState) => state.attendanceFilters,
);

export const selectAllActiveStudents = createSelector(selectAllStudents, (students: IStudent[]) =>
  students.filter((student) => student.enrolmentStatus != 'Cancelled' && student.enrolmentStatus != 'Inactive'),
);

export const selectAllPendingStudents = createSelector(selectAllStudents, (students: IStudent[]) =>
  students.filter(
    (student) =>
      student.enrolmentStatus != 'Active' &&
      student.enrolmentStatus != 'Cancelled' &&
      student.enrolmentStatus != 'Inactive',
  ),
);

export const selectAllFormerStudents = createSelector(selectAllStudents, (students: IStudent[]) =>
  students.filter((student) => student.enrolmentStatus === 'Inactive'),
);

export const selectStudentById = (studentId: string) =>
  createSelector(selectStudentState, (state: StudentState) =>
    state.students.find((student) => student.id === studentId),
  );

export const selectStudentLoadingStatus = createSelector(
  selectStudentState,
  selectLoadingInitialGuardian,
  (studentState, loadingInitialGuardian) => {
    return studentState.loading || loadingInitialGuardian;
  },
);

// ✅ Get emergency contacts from selected student
export const selectEmergencyContactsOfSelectedStudent = createSelector(selectSelectedStudent, (selectedStudent) =>
  selectedStudent ? selectedStudent.emergencyContacts || [] : [],
);
//create selector that returns the student with {id} Avatar Data
export const selectStudentAvatarDataById = (studentId: string, size: number) =>
  createSelector(
    selectStudentById(studentId),
    (student): UserAvatarData => ({
      id: student?.id ?? 'no-id',
      pictureUrl: student?.pictureUrl ?? 'assets/images/curro.webp',
      borderColour: student?.colour ?? '#14448F',
      isGuardian: false,
      routeEnabled: true,
      name: student?.preferredName,
      enrolmentPercent: 100, //getEnrolmentPercent(student?.enrolmentStatus ?? '') ?? 100,
      size,
    }),
  );

export const selectDOStudentAvatarDataById = (studentId: string, size: number) =>
  createSelector(
    selectStudentById(studentId),
    (student): UserAvatarData => ({
      id: student?.id ?? 'no-id',
      pictureUrl: student?.pictureUrl ?? '👤',
      borderColour: student?.colour ?? '#14448F',
      isGuardian: false,
      routeEnabled: false,
      name: student?.preferredName,
      enrolmentPercent: 100,
      size,
    }),
  );

export const selectStudentColourById = (studentId: string) =>
  createSelector(selectStudentState, (state: StudentState) => {
    const student = state.students.find((student) => student.id === studentId);
    return student ? student.colour : '#14448F';
  });

export const selectStudentByColour = (colourHex: string) =>
  createSelector(selectStudentState, (state: StudentState) => {
    const student = state.students.find((student) => student.colour === colourHex);
    if (student) {
      return student;
    } else {
      return {} as IStudent;
    }
  });

export const selectSelectedStudentFirstName = createSelector(
  selectSelectedStudent,
  (student: IStudent | null) => student?.firstname,
);

export const selectSelectedStudentNumber = createSelector(
  selectSelectedStudent,
  (student: IStudent | null) => student?.studentNumber || '',
);

export const selectIsSelectedStudentActive = createSelector(selectSelectedStudent, (student: IStudent | null) => {
  if (student) {
    return student.enrolmentStatus !== 'Inactive' && student.enrolmentStatus !== 'Cancelled';
  }
  return false;
});

export const selectIsSelectedStudentInActive = createSelector(selectSelectedStudent, (student: IStudent | null) => {
  if (student) {
    return student.enrolmentStatus === 'Inactive' || student.enrolmentStatus === 'Cancelled';
  }
  return false;
});

export const selectStudentAvatarDataByColour = (colourHex: string, size: number) =>
  createSelector(
    selectStudentByColour(colourHex),
    (student): UserAvatarData => ({
      id: student?.id ?? 'no-id',
      pictureUrl: student?.pictureUrl ?? 'assets/images/curro.webp',
      borderColour: student?.colour ?? '#14448F',
      isGuardian: false,
      size: size,
    }),
  );

export const selectSchoolIdByLearnerId = (learnerId: string) =>
  createSelector(selectAllStudents, (students: IStudent[]) => {
    const student = students.find((student) => student.id === learnerId);
    return student ? student.schoolId : '';
  });

export const selectStudentNameById = (studentId: string) =>
  createSelector(selectStudentById(studentId), (student) => student?.firstname + ' ' + student?.lastname);

export const selectStudentNamesByIds = (studentIds: string[]) =>
  createSelector(selectAllStudents, (students) =>
    students.filter((student) => studentIds.includes(student.id)).map((student) => student.firstname),
  );

export const selectStudentsForAccount = (accountCode: string, companyCode: string) =>
  createSelector(selectAllStudents, (students: IStudent[]) =>
    students.filter(
      (student) =>
        student.school?.accountCode === accountCode &&
        student.school?.myHubCompanySettings?.companyCode === companyCode,
    ),
  );

export const selectAttendanceRecordWithFilters = createSelector(
  selectSelectedStudentId,
  selectStudentState,
  selectStudentAttendanceFilters,
  (selectedStudentId, state, attendanceFilters) => {
    const attendanceRecords = selectedStudentId ? state.attendanceRecords[selectedStudentId] || [] : [];

    if (!attendanceRecords || attendanceRecords.length === 0) {
      return [] as IAttendanceRecord[];
    }

    let filteredAttendance = attendanceRecords.filter((record) => {
      // Filter by status/type - only 4 allowed statuses
      const statusFilters = {
        Absent: attendanceFilters.Absent,
        Late: attendanceFilters.Late,
        'Left early': attendanceFilters.LeftEarly,
        'Missed subjects': attendanceFilters.MissedSubjects,
      };

      if (!statusFilters[record.status]) {
        return false;
      }

      // Filter by term using the term from the record
      if (record.term) {
        switch (record.term) {
          case 'Term 1':
            return attendanceFilters.Term1;
          case 'Term 2':
            return attendanceFilters.Term2;
          case 'Term 3':
            return attendanceFilters.Term3;
          case 'Term 4':
            return attendanceFilters.Term4;
          default:
            // If we can't match the term, include it if any term filter is active
            return (
              attendanceFilters.Term1 || attendanceFilters.Term2 || attendanceFilters.Term3 || attendanceFilters.Term4
            );
        }
      }

      // If no term data, include if any term filter is active
      return attendanceFilters.Term1 || attendanceFilters.Term2 || attendanceFilters.Term3 || attendanceFilters.Term4;
    });

    if (!attendanceFilters.YearToDate) {
      // Sort the attendance records by date descending and return the latest 10
      filteredAttendance = filteredAttendance.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 10);
    }

    return filteredAttendance;
  },
);

export const selectAttendanceRecordTotal = createSelector(
  selectSelectedStudentId,
  selectStudentState,
  selectStudentAttendanceFilters,
  (selectedStudentId, state, attendanceFilters) => {
    const attendance = selectedStudentId ? state.attendanceRecords[selectedStudentId] || [] : [];
    const tally = {
      Late: 0,
      Absent: 0,
      LeftEarly: 0,
      MissedSubjects: 0,
    };

    attendance.forEach((record) => {
      switch (record.status) {
        case 'Late':
          tally.Late++;
          break;
        case 'Absent':
          tally.Absent++;
          break;
        case 'Left early':
          tally.LeftEarly++;
          break;
        case 'Missed subjects':
          tally.MissedSubjects++;
          break;
      }
    });

    return [
      { title: 'Absent', tally: tally.Absent, disabled: !attendanceFilters.Absent },
      { title: 'Late', tally: tally.Late, disabled: !attendanceFilters.Late },
      { title: 'Left early', tally: tally.LeftEarly, disabled: !attendanceFilters.LeftEarly },
      { title: 'Missed subjects', tally: tally.MissedSubjects, disabled: !attendanceFilters.MissedSubjects },
    ];
  },
);

export const selectStudentWithClassId = (classId: string) =>
  createSelector(selectAllStudents, (students: IStudent[]) =>
    students.find((student) => student.classIds.includes(classId)),
  );

export const selectSelectedStudentEnrolmentStatus = createSelector(
  selectSelectedStudent,
  (student: IStudent | null) => {
    if (student) {
      return student.enrolmentStatus; // Return the enrolment status of the selected student
    } else {
      return 'Active'; // Return null or a default value if student is not selected
    }
  },
);

export const selectSelectedStudentEnrolmentDocuments = createSelector(
  selectSelectedStudent,
  (student: IStudent | null) => {
    if (student) {
      return student.enrolmentDocuments; // Return the enrolment status of the selected student
    } else {
      return []; // Return null or a default value if student is not selected
    }
  },
);

export const selectSelectedStudentEnrolmentDocumentWithTitle = (title: string) =>
  createSelector(selectSelectedStudentEnrolmentDocuments, (documents) => {
    if (!documents) return null;
    const normalizedTitle = title.toLowerCase().replace(/-/g, ' ');
    return documents.find((document) => document.title.toLowerCase().replace(/-/g, ' ') === normalizedTitle) || null;
  });

export const selectStudentsMissingPicture = createSelector(selectAllActiveStudents, (activeStudents) => {
  // Find the first student missing a profile picture
  const studentMissingPicture = activeStudents.find((student) => !student.pictureUrl);
  const studentId = studentMissingPicture ? studentMissingPicture.id : null;

  if (!studentMissingPicture) {
    // All students have pictures
    return {
      taskComplete: true,
      url: '',
      message: 'All students have pictures.',
    };
  } else {
    // At least one student is missing a picture
    return {
      taskComplete: false,
      url: 'learners/' + studentId + '/profile/picture', // Redirect to profile to add a picture
      message: `Add a profile picture for ${studentMissingPicture.firstname}`,
      studentId: studentMissingPicture.id,
    };
  }
});

export const selectStudentNextSteps = createSelector(
  selectAllPendingStudents, // This selector should return a single student object
  (students) => {
    if (students[0]) {
      const { enrolmentStatus } = students[0];

      let message = '';
      let url = '';
      let taskComplete = false;
      let studentId = null;

      // Use switch for handling different enrolment statuses
      switch (enrolmentStatus) {
        case 'Review':
          taskComplete = true;
          message = 'Awaiting feedback.';
          url = 'enrolments/status';
          break;
        case 'Application Submitted':
          taskComplete = true;
          message = 'Reviewing enrollment.';
          url = 'enrolments/status';
          break;
        case 'Feedback':
          taskComplete = false;
          message = 'Sign required enrollment documents.';
          url = 'enrolments/documents';
          studentId = students[0].id;
          break;
        case 'Documents Submitted':
          taskComplete = false;
          message = 'Pay enrolment fee.';
          url = 'enrolments/status';
          studentId = students[0].id;
          break;
        case 'Paid':
          taskComplete = true;
          message = 'Enrollment payment completed.';
          url = '';
          break;
        default:
          message = 'Unknown enrollment status.';
          url = ''; // Default URL if status is unrecognized
      }

      // Return the next steps information for this single student
      return {
        taskComplete, // Whether the task is complete
        url, // The URL to guide the next action
        message,
        studentId, // The message guiding the next steps
      };
    }

    return null;
  },
);

// New attendance selectors
export const selectStudentAttendanceRecords = (studentId: string) =>
  createSelector(selectStudentState, (state: StudentState) => state.attendanceRecords[studentId] || []);

export const selectStudentAttendanceLoading = (studentId: string) =>
  createSelector(selectStudentState, (state: StudentState) => state.attendanceLoading[studentId] || false);

export const selectSelectedStudentAttendance = createSelector(
  selectSelectedStudentId,
  selectStudentState,
  (selectedStudentId, state) => (selectedStudentId ? state.attendanceRecords[selectedStudentId] || [] : []),
);

export const selectSelectedStudentAttendanceLoading = createSelector(
  selectSelectedStudentId,
  selectStudentState,
  (selectedStudentId, state) => (selectedStudentId ? state.attendanceLoading[selectedStudentId] || false : false),
);

export const selectAllStudentsThatLoggedInUserHasAdminAccessTo = createSelector(
  selectAllStudents,
  selectLoggedInGuardian,
  (students, guardian) => {
    // Filter students based on the logged-in guardian's access
    return students.filter((student) => {
      // Check if the guardian has access to this student
      const learnerList: ILearnerAccess[] = guardian.learnerAccess || [];
      return learnerList.some((learner) => learner.learnerId === student.id && learner.userType === 'Admin');
    });
  },
);

export const selectAllActiveStudentIds = createSelector(selectAllActiveStudents, (students) =>
  students.map((student) => student.id),
);

export const selectAllActiveStudentAvatarDataWithCompanyCode = (size: number) =>
  createSelector(selectAllActiveStudents, (students): (UserAvatarData & { companyCode: string })[] =>
    students.map((student) => ({
      id: student.id,
      pictureUrl: student.pictureUrl || 'assets/images/curro.webp',
      borderColour: student.colour || '#14448F',
      isGuardian: false,
      routeEnabled: true,
      name: student.preferredName,
      enrolmentPercent: 100,
      size,
      companyCode: student.school?.myHubCompanySettings?.companyCode || student.companyCode || '',
    })),
  );

export const selectStudentCompanyCodeByAccountCode = (accountCode: string) =>
  createSelector(selectAllStudents, (students) => {
    const value = students.find((student) => student.accountCode === accountCode)?.companyCode;
    return value;
  });

export const selectStudentSchoolByAccountCode = (accountCode: string) =>
  createSelector(selectAllStudents, (students) => {
    const value = students.find((student) => student.school?.accountCode === accountCode);
    return value?.school?.myHubCompanySettings?.companyName;
  });
